import { hasConsent } from './consentManager';

declare global {
  interface Window {
    clarity: any;
  }
}

const CLARITY_PROJECT_ID = 'qutf4uofuy';

/**
 * Initialize Microsoft Clarity (Session Recording & Analytics)
 * Only called when user has given analytics consent
 */
export const initClarity = (): void => {
  // Check consent before initializing
  if (!hasConsent('analytics')) {
    console.log('Microsoft Clarity initialization skipped - no consent');
    return;
  }

  try {
    // Check if already initialized
    if (window.clarity) {
      console.log('Microsoft Clarity already initialized');
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;

    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }

    console.log('Microsoft Clarity initialized');
  } catch (error) {
    console.error('Failed to initialize Clarity:', error);
  }
};

/**
 * Disable Clarity tracking
 * Note: Clarity doesn't have a built-in disable method,
 * so we prevent initialization and clear existing instance
 */
export const disableClarity = (): void => {
  try {
    if (window.clarity) {
      // Clarity doesn't have a disable method, but we can prevent future calls
      window.clarity = undefined;
      console.log('Microsoft Clarity disabled');
    }
  } catch (error) {
    console.error('Error disabling Clarity:', error);
  }
};

/**
 * Track page view in Clarity
 * Checks consent before tracking
 */
const pageView = (route: string): void => {
  if (!hasConsent('analytics')) {
    return;
  }

  try {
    if (window.clarity) {
      window.clarity("set", "page_view", route);
    }
  } catch (error) {
    console.error('Clarity tracking error:', error);
  }
};

/**
 * Track custom event in Clarity
 * Checks consent before tracking
 */
const event = (eventName: string): void => {
  if (!hasConsent('analytics')) {
    return;
  }

  try {
    if (window.clarity) {
      window.clarity("event", eventName);
    }
  } catch (error) {
    console.error('Clarity tracking error:', error);
  }
};

const clarity = {
  initialize: initClarity,
  disable: disableClarity,
  pageView,
  event
};

export default clarity; 